import { z } from 'zod';
import { API_BASE, apiGet, apiSend } from '@/lib/api';

/**
 * NAGRANIA Z KAMERY SPS (06.10.2026) — lista, ustawienia wyspy i adres pliku.
 *
 * ⚠️ SCHEMATY ŁAGODNE TAM, GDZIE SERWER MOŻE ODDAĆ `null`. Zod ostrzejszy niż serwis pokazuje
 * udany odczyt jako błąd — to już raz kosztowało nas zgłoszenie „zapis się nie udał", choć się udał.
 */

/** Te same granice co w API (`nagrania.ts`) — pilnuje tego spek po stronie API. */
export const MAKS_OKRES_MIES = 24;
export const MAKS_LIMIT_MB = 20;
/** Kasowanie na maszynie: wiek nagrania w dniach, 1–365 (jak `MAKS_USUN_PO_DNIACH` w API). */
export const MAKS_USUN_PO_DNIACH = 365;

export const NagranieSchema = z.object({
  id: z.string(),
  automat: z.object({ id: z.string(), nazwa: z.string(), vmid: z.number() }),
  operationId: z.number(),
  kody: z.array(z.string()),
  start: z.string(),
  koniec: z.string(),
  nagranieOd: z.string(),
  nagranieDo: z.string(),
  sekund: z.number(),
  rozmiar: z.number(),
  stan: z.enum(['OCZEKUJE', 'WYSLANE', 'ZA_DUZE']),
  /** Kopia MP4 do odtwarzania w przeglądarce; brak pola traktujemy jak „jeszcze nie ma". */
  konwersja: z.enum(['CZEKA', 'GOTOWE', 'BLAD']).optional(),
  plik: z.string(),
  osoba: z.object({ numer: z.string().nullish(), nazwa: z.string().nullish() }).nullish(),
  towary: z.array(z.object({ numer: z.string().nullish(), nazwa: z.string().nullish(), ilosc: z.number() })),
});
export type Nagranie = z.infer<typeof NagranieSchema>;

export const ListaNagranSchema = z.object({ nagrania: z.array(NagranieSchema) });

export const UstawieniaNagranSchema = z.object({
  okresMies: z.number(),
  limitMb: z.number(),
  pobierane: z.array(z.string()),
  /**
   * KASOWANIE NA MASZYNIE (12.10.2026). `default`, bo instancja starsza od tej zmiany tych pól nie
   * odda — a schemat ostrzejszy od odpowiedzi zamienia udany odczyt w błąd.
   */
  usuwane: z.array(z.string()).default([]),
  usunPoDniach: z.number().default(30),
});
export type UstawieniaNagran = z.infer<typeof UstawieniaNagranSchema>;

export function fetchNagrania(groupId: string, f: { od?: string; do?: string } = {}) {
  const q = new URLSearchParams({ groupId });
  if (f.od) q.set('od', f.od);
  if (f.do) q.set('do', f.do);
  return apiGet(`/nagrania?${q.toString()}`, ListaNagranSchema);
}

export const fetchUstawieniaNagran = (groupId: string) =>
  apiGet(`/nagrania/ustawienia?groupId=${encodeURIComponent(groupId)}`, UstawieniaNagranSchema);

export const zapiszUstawieniaNagran = (dto: UstawieniaNagran & { groupId: string }) =>
  apiSend('PUT', '/nagrania/ustawienia', dto, UstawieniaNagranSchema);

/** Katalog nagrań SPS na komputerze automatu — rozmiar od agenta (12.10.2026). */
const KatalogNagranSchema = z.object({
  automat: z.object({ id: z.string(), nazwa: z.string(), vmid: z.number() }),
  katalog: z.string(),
  plikow: z.number(),
  bajtow: z.number(),
  usunietych: z.number(),
  zmierzonoO: z.string(),
});
export type KatalogNagran = z.infer<typeof KatalogNagranSchema>;

export const fetchKatalogiNagran = (groupId: string) =>
  apiGet(`/nagrania/katalogi?groupId=${encodeURIComponent(groupId)}`, z.array(KatalogNagranSchema));

/** Rozmiar katalogu do pokazania — GB od gigabajta, poniżej MB. */
export const rozmiarKatalogu = (b: number) =>
  b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(1)} GB` : `${(b / 1024 ** 2).toFixed(0)} MB`;

/**
 * ADRES PLIKU — zwykły link, nie `fetch` do pamięci.
 *
 * ⚠️ Sesja jedzie ciasteczkiem, więc przeglądarka pobiera plik sama, strumieniem. Ściąganie do
 * `Blob` trzymałoby całe nagranie w pamięci karty tylko po to, żeby je potem zapisać.
 */
export const adresNagrania = (groupId: string, id: string) =>
  `${API_BASE}/nagrania/${encodeURIComponent(id)}/plik?groupId=${encodeURIComponent(groupId)}`;

/**
 * KOPIA MP4 DO ODTWARZACZA — prosto w `<video src>`. Przewijanie działa, bo trasa obsługuje `Range`,
 * a sesja jedzie ciasteczkiem tak samo jak przy pobieraniu.
 */
export const adresWideo = (groupId: string, id: string) =>
  `${API_BASE}/nagrania/${encodeURIComponent(id)}/wideo?groupId=${encodeURIComponent(groupId)}`;

/**
 * KODY OPERACJI SPS DO ZAZNACZENIA — ze słownika `FunctionTransactions` (kopia gestamp, 06.10.2026).
 *
 * ⚠️ `DD` nie ma w tym słowniku, ale wymienił go Kamil wśród nagrań do pominięcia — jest na liście,
 * żeby było widać, że nie jest pobierany. Kod zapisany w ustawieniach, a nieobecny tutaj, ekran
 * i tak pokazuje (patrz `kodyDoPokazania`).
 */
export const KODY_NAGRAN: readonly { kod: string; rodzaj: string }[] = [
  { kod: 'RR', rodzaj: 'wrzut' },
  { kod: 'WR', rodzaj: 'doRegeneracji' },
  { kod: 'WRD', rodzaj: 'doRegeneracji' },
  { kod: 'RRN', rodzaj: 'zRegeneracji' },
  { kod: 'RRG', rodzaj: 'zRegeneracji' },
  { kod: 'RRU', rodzaj: 'zRegeneracji' },
  { kod: 'RRS', rodzaj: 'zlom' },
  { kod: 'RS', rodzaj: 'zlom' },
  { kod: 'RN', rodzaj: 'zwrot' },
  { kod: 'RG', rodzaj: 'zwrot' },
  { kod: 'RU', rodzaj: 'zwrot' },
  { kod: 'WN', rodzaj: 'pobranie' },
  { kod: 'WG', rodzaj: 'pobranie' },
  { kod: 'WU', rodzaj: 'pobranie' },
  { kod: 'REN', rodzaj: 'uzupelnienie' },
  { kod: 'REG', rodzaj: 'uzupelnienie' },
  { kod: 'REU', rodzaj: 'uzupelnienie' },
  { kod: 'IA', rodzaj: 'korekta' },
  { kod: 'DD', rodzaj: 'inne' },
];

/** Znane kody plus te z ustawień, których lista nie zna — żeby żadnego nie dało się zgubić. */
export function kodyDoPokazania(u: UstawieniaNagran | undefined): { kod: string; rodzaj: string }[] {
  const znane = new Set(KODY_NAGRAN.map((k) => k.kod));
  const obce = [...new Set([...(u?.pobierane ?? []), ...(u?.usuwane ?? [])])]
    .filter((k) => !znane.has(k))
    .sort()
    .map((kod) => ({ kod, rodzaj: 'inne' }));
  return [...KODY_NAGRAN, ...obce];
}

/** Przełączenie kodu na liście — bez powtórzeń, w stałej kolejności. */
export function przelaczKod(lista: readonly string[], kod: string): string[] {
  return lista.includes(kod) ? lista.filter((k) => k !== kod) : [...lista, kod].sort();
}

/** Czas ścienny automatu do pokazania: `2026-10-05T21:57:49` → `2026-10-05 21:57:49`. */
export const czasNagrania = (s: string) => s.replace('T', ' ');

export const rozmiarMb = (b: number) => (b / (1024 * 1024)).toFixed(b < 10 * 1024 * 1024 ? 2 : 1);

/**
 * DŁUGOŚĆ NAGRANIA OD MINUTY W GÓRĘ — `m:ss` albo `g:mm:ss`; krótsze ekran pisze w sekundach.
 *
 * ⚠️ Nagranie „godzinami" (operacja, która się nie skończyła) to właśnie to, przed czym chroni limit
 * wyspy — „7995 s" nie mówi tego na pierwszy rzut oka, „2:13:15" mówi.
 */
export function dlugoscNagrania(sekund: number): string | null {
  if (sekund < 60) return null;
  const g = Math.floor(sekund / 3600);
  const m = Math.floor((sekund % 3600) / 60);
  const s = String(sekund % 60).padStart(2, '0');
  return g > 0 ? `${g}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}
